import { PRESENT_VALUE_SENTINEL } from '@gonfalon/dataviz-experimental/types';
import type { FieldOptions, Operator, ParsedToken } from '@gonfalon/dataviz-experimental/types';

// -- Payload types ------------------------------------------------------------

export type FilterCondition = {
  field: string;
  op: Operator;
  /**
   * Always serialised as a string (or array of strings). The chart-data
   * endpoint accepts string values uniformly and the parser stringifies
   * native numbers/booleans on the way out so the payload shape is
   * consistent regardless of how the FILTER token's value was typed.
   */
  value: string | string[];
};

export type FilterConditionItem = FilterCondition | FiltersClause;
export type FiltersClause = { and: FilterConditionItem[] } | { or: FilterConditionItem[] };

export type SelectField = {
  field: string;
  expression: string;
};

export type TimeRange = {
  from: number;
  to: number;
  interval: string;
  timezone: string;
};

export type DataSource = {
  domain: string;
  kind: string;
  timeRange: TimeRange;
};

export type GroupByField = {
  field: string;
};

export type QueryPayload = {
  projectKey: string;
  environmentKey: string;
  dataSource: DataSource;
  select: SelectField[];
  filters: FiltersClause;
  groupBy: GroupByField[];
};

export type QueryPayloadContext = Omit<QueryPayload, 'filters'>;

// -- Helpers ------------------------------------------------------------------

// Maps each operator to its semantic inverse. startsWith, endsWith, and ** have no
// native inverse in the chart-data API, so they are absent — `tokenToCondition` rejects
// negation for those at the API boundary instead of silently producing a wrong payload.
const NEGATED_OP: Partial<Record<Operator, Operator>> = {
  '=': '!=',
  '!=': '=',
  '>': '<=',
  '>=': '<',
  '<': '>=',
  '<=': '>',
  '//': '!//',
  '!//': '//',
};

type FilterToken = Extract<ParsedToken, { type: 'FILTER' }>;

function tokenToCondition(token: FilterToken, valueType: FieldOptions['valueType']): FilterConditionItem {
  // The '__present__' sentinel means "field has any non-empty value". The chart-data API
  // has no exists/is_not_null operator, so emit:
  //   - boolean field → `(field=false OR field=true)` (negated: AND of both !=) — there
  //     are exactly two possible values and `field != ""` doesn't survive the API's
  //     boolean coercion. The OR-expansion enumerates them explicitly.
  //   - any other field → `field != ""` (negated: `field == ""`).
  const isPresentSentinel =
    token.value === PRESENT_VALUE_SENTINEL ||
    (Array.isArray(token.value) && token.value.length === 1 && token.value[0] === PRESENT_VALUE_SENTINEL);
  if (isPresentSentinel) {
    // "Absence" (NOT EXISTS) is expressed as either the `!` negation prefix OR
    // the `!=` operator — XOR them, matching filterTokenToAntlr /
    // parseOneAntlrExists. Keying off `token.negated` alone would misread
    // `field:!=:__present__` (the form NOT EXISTS encoding) as EXISTS and invert
    // the user's intent.
    const isAbsent = !!token.negated !== (token.operator === '!=');
    if (valueType === 'boolean') {
      const booleanValues: FilterCondition[] = [
        { field: token.field, op: isAbsent ? '!=' : '=', value: 'false' },
        { field: token.field, op: isAbsent ? '!=' : '=', value: 'true' },
      ];
      return isAbsent ? { and: booleanValues } : { or: booleanValues };
    }
    return { field: token.field, op: isAbsent ? '=' : '!=', value: '' };
  }

  let op: Operator;
  if (token.negated) {
    const negated = NEGATED_OP[token.operator];
    if (!negated) {
      throw new Error(`Cannot negate operator '${token.operator}' — no inverse exists in the chart-data API`);
    }
    op = negated;
  } else {
    op = token.operator;
  }

  // The parser emits an array for unquoted comma-separated values (field:=:a,b), but
  // stringifyToken quotes any value containing a comma, so a multi-select round-trip
  // produces a single quoted string (field:=:"a,b") that the parser keeps as a literal.
  // Split *string* values on commas so both paths produce the same result. Native
  // numeric / boolean values get stringified below — the chart-data endpoint takes
  // string values uniformly and consistent typing keeps the payload portable.
  //
  // Regex operators (`//` and `!//`) take a single regex literal whose body may legally
  // contain commas (e.g. `/foo,bar/`). Skip the comma-split for them so the regex is
  // forwarded to the payload as one value rather than being shredded into multiple.
  const isRegexOp = token.operator === '//' || token.operator === '!//';
  let rawValues: string[];
  if (Array.isArray(token.value)) {
    rawValues = token.value.map(String);
  } else if (typeof token.value === 'string' && !isRegexOp) {
    rawValues = token.value
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean);
  } else {
    rawValues = [String(token.value)];
  }

  if (rawValues.length === 0) {
    // Token value was a string of only commas/whitespace (e.g., ",," or " ,"). Treat
    // as empty rather than passing the raw delimiter string through to the API.
    return { field: token.field, op, value: '' };
  }
  if (rawValues.length === 1) {
    return { field: token.field, op, value: rawValues[0] };
  }

  // Choose the boolean combinator based on operator polarity. Positive operators
  // (=, contains, startsWith, etc.) expand to OR — "matches any of these values".
  // Negative operators expand to AND — "matches none of these values"; using OR for
  // != would yield `field != a OR field != b`, which is always true when a ≠ b.
  const items = rawValues.map((v): FilterCondition => ({ field: token.field, op, value: v }));
  return op === '!=' ? { and: items } : { or: items };
}

// -- Main function ------------------------------------------------------------

function buildClause(
  tokens: ParsedToken[],
  getFieldValueType: (field: string) => FieldOptions['valueType'],
): FiltersClause {
  // Collect conditions and operators in document order.
  const entries: Array<FilterConditionItem | 'AND' | 'OR' | 'THEN'> = [];

  for (const token of tokens) {
    if (token.type === 'FILTER') {
      entries.push(tokenToCondition(token, getFieldValueType(token.field)));
    } else if (token.type === 'EXPRESSION') {
      entries.push(buildClause(token.inner, getFieldValueType));
    } else if (token.type === 'LOGIC_BOOL') {
      entries.push(token.value);
    }
    // FREE_TEXT tokens have no API representation
  }

  // AND binds tighter than OR: split at OR operators so each segment
  // between ORs is treated as an implicit AND group.
  //   a AND b OR c  →  { or: [{ and: [a, b] }, c] }
  //   a AND b AND c →  { and: [a, b, c] }
  //   a OR b OR c   →  { or: [a, b, c] }
  const orSegments: FilterConditionItem[][] = [[]];
  for (const entry of entries) {
    if (entry === 'OR') {
      orSegments.push([]);
    } else if (entry !== 'AND' && entry !== 'THEN') {
      orSegments[orSegments.length - 1].push(entry);
    }
  }

  if (orSegments.length === 1) {
    return { and: orSegments[0] };
  }

  return {
    or: orSegments.map((segment) => (segment.length === 1 ? segment[0] : { and: segment })),
  };
}

/**
 * Converts a parsed token array into a query API payload.
 *
 * FILTER tokens become filter conditions. EXPRESSION tokens (parenthesized
 * groups) are recursively converted into nested FiltersClause objects.
 * AND/OR operators are handled with correct precedence (AND binds tighter).
 * FREE_TEXT tokens are ignored.
 *
 * `fieldOptions` is optional; when supplied, the field's `valueType` drives
 * type-specific payload shapes — e.g. a present-sentinel filter on a boolean
 * field emits the `(field=false OR field=true)` enumeration instead of the
 * `field != ""` shape used for string fields.
 */
export function buildDatavizQuery(
  tokens: ParsedToken[],
  context: QueryPayloadContext,
  fieldOptions?: FieldOptions[],
): QueryPayload {
  const valueTypeByField = new Map(fieldOptions?.map((opt) => [opt.field, opt.valueType]) ?? []);
  const getFieldValueType = (field: string) => valueTypeByField.get(field);
  return {
    ...context,
    filters: buildClause(tokens, getFieldValueType),
  };
}
